import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useSiteSettings } from "@/contexts/SettingsContext";

/**
 * Promo banner shown at the top of every page when enabled in settings.
 * Can be dismissed by the visitor (persisted in localStorage, keyed by content).
 */
export default function PromoBanner() {
  const { settings } = useSiteSettings();
  const enabled = settings?.banner_enabled;
  const text = settings?.banner_text || "";
  const linkUrl = settings?.banner_link_url || "";
  const linkLabel = settings?.banner_link_label || "";
  const bg = settings?.banner_bg_color || "#059669";

  // Dismissal is tied to the text so a new campaign reappears.
  const dismissKey = `promo-banner-dismissed:${text}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (enabled && text) {
      setDismissed(localStorage.getItem(dismissKey) === "1");
    }
  }, [enabled, text, dismissKey]);

  if (!enabled || !text || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  return (
    <div
      className="w-full text-white text-sm relative"
      style={{ background: bg }}
      data-testid="promo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 pr-10 flex items-center justify-center gap-2 text-center">
        <span className="font-medium leading-snug">{text}</span>
        {linkUrl && linkLabel && (
          <a
            href={linkUrl}
            className="underline font-semibold hover:opacity-90 ml-1 whitespace-nowrap"
            data-testid="promo-banner-link"
          >
            {linkLabel} →
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hover:bg-white/15 flex items-center justify-center transition"
        aria-label="Fermer"
        data-testid="promo-banner-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
